import createClient from 'openapi-fetch'
import type { paths } from '../types/backend-openapi'
import { getChatApiRoute, getChatApiUrl } from '@shared/utils/ChatBackendUtils'

// @ts-ignore - only defined here because importing from runtimeutils caused an error
export const isElectron = typeof process !== 'undefined' && process.type === 'renderer'

/**
 * Re-exported rather than reimplemented: this module used to carry its own copy
 * of the channel-based resolution, which meant every chat call went to
 * staging/production regardless of Settings > Backend Host or the ephemeral
 * backend auto-detect. The shared implementation is the only one now.
 */
export { getChatApiRoute, getChatApiUrl }

/**
 * New v1 client, should be used from now on.
 *
 * The host is resolved per request, not captured at module load. It used to be
 * captured, on the reasoning that main resolves the backend during startup and
 * changing it restarts the app — both true, and still not enough: the renderer
 * has its *own* settings store, filled asynchronously by StoreSyncManager,
 * while `get()` reads a synchronous in-memory cache. This module is imported
 * before that sync lands, so `getChatApiRoute()` saw an empty override and
 * froze the channel default (staging) forever. Every v1Client call then went to
 * staging while every call-time `getChatApiRoute()` consumer honored Settings >
 * Backend Host — one token, two backends, 401s on exactly the surfaces whose
 * data comes from this client.
 */
export const v1Client = createClient<paths>({
  baseUrl: getChatApiRoute('api/v1'),
  fetch: (request) => {
    const current = getChatApiRoute('api/v1')
    // Rebuild only when the resolved host has moved on from whatever baseUrl
    // was at construction; the common case allocates nothing extra.
    const rebased = new URL(request.url, current)
    const target = new URL(current)
    if (rebased.origin === target.origin) {
      return globalThis.fetch(request)
    }
    rebased.protocol = target.protocol
    rebased.host = target.host
    return globalThis.fetch(new Request(rebased, request))
  },
})
